import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Table, Form, Select, Input, Button, Tag, Card, Statistic, Row, Col, DatePicker, message, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;
const { RangePicker } = DatePicker;

const DEVICE_TYPE_MAP = {
  belt_metal_detector: '皮带式金属检测机',
};

const actionColors = {
  LOGIN: 'green',
  LOGOUT: 'orange',
  CREATE: 'blue',
  UPDATE: 'cyan',
  DELETE: 'red',
  DELETE_REQUEST: 'red',
  APPROVE_DELETE: 'green',
  REJECT_DELETE: 'orange',
  UPLOAD: 'purple',
  DELETE_FILE: 'magenta',
  RESTORE: 'blue',
  PERMANENT_DELETE: 'red',
  CREATE_APPROVAL: 'blue',
  UPDATE_APPROVAL: 'cyan',
  DELETE_APPROVAL: 'red',
  APPROVE_EXECUTE: 'green',
  APPROVE_EXECUTE_FAILED: 'red',
  REJECT_APPROVAL: 'orange',
  DOWNLOAD_3D_APPROVAL: 'purple'
};

const actionNames = {
  LOGIN: '登录',
  LOGOUT: '登出',
  CREATE: '创建图纸',
  UPDATE: '修改图纸',
  DELETE: '逻辑删除',
  DELETE_REQUEST: '申请删除',
  APPROVE_DELETE: '同意删除',
  REJECT_DELETE: '拒绝删除',
  UPLOAD: '上传文件',
  DELETE_FILE: '删除文件',
  RESTORE: '恢复图纸',
  PERMANENT_DELETE: '永久删除',
  CREATE_APPROVAL: '创建审批',
  UPDATE_APPROVAL: '修改审批',
  DELETE_APPROVAL: '删除审批',
  APPROVE_EXECUTE: '审批通过执行',
  APPROVE_EXECUTE_FAILED: '审批执行失败',
  REJECT_APPROVAL: '拒绝审批',
  DOWNLOAD_3D_APPROVAL: '3D下载审批'
};

const SystemLogPage = () => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ todayCount: 0, actionStats: [], userStats: [] });
  const [actions, setActions] = useState([]);
  const { isDarkMode, getBackgroundColor, getContentBg, getTextColor, getTextColorSecondary, getBorderColor } = useTheme();

  const detailKeyMap = {
    username: '用户名',
    drawing_id: '图纸ID',
    approved: '审批结果',
    creator: '制图人',
    deviceType: '设备类型',
    updatedFields: '更新字段',
    fileType: '文件类型',
    fileName: '文件名',
    drawingNo: '图纸编号',
    device_type: '设备类型',
    drawDate: '制图日期',
    '更新字段': '更新字段',
    '更新文件': '更新文件',
    '制图人': '制图人',
    '制图日期': '制图日期',
    '设备类型': '设备类型',
    '图纸编号': '图纸编号',
    '文件类型': '文件类型',
    '文件名': '文件名',
    '用户名': '用户名',
    '图纸ID': '图纸ID',
    '审批结果': '审批结果',
    action: '操作',
    source_id: '源图纸ID',
    source_no: '源图纸编号'
  };

  const detailValueMap = {
    approved: (val) => {
      if (val === true || val === 'true') return '同意';
      if (val === false || val === 'false') return '拒绝';
      return val;
    },
    deviceType: { belt_metal_detector: '皮带式金属检测机' },
    device_type: { belt_metal_detector: '皮带式金属检测机' },
    action: { clone: '复制', '逻辑删除': '逻辑删除', '永久删除': '永久删除', '从回收站恢复': '从回收站恢复' }
  };

  const fetchLogs = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: params.page || currentPage,
        pageSize: params.pageSize || pageSize,
        ...params
      };
      const res = await api.get('/system-logs', { params: queryParams });
      setData(res.data.data);
      setCurrentPage(res.data.page);
      setPageSize(res.data.pageSize);
      setTotal(res.data.total);
    } catch (err) {
      message.error('获取日志失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/system-logs/stats');
      setStats(res.data);
    } catch (err) {}
  };

  const fetchActions = async () => {
    try {
      const res = await api.get('/system-logs/actions');
      setActions(res.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchActions();
  }, [fetchLogs]);

  const handleSearch = (values) => {
    const params = { ...values, page: 1, pageSize: pageSize };
    if (values.dateRange && values.dateRange.length === 2) {
      params.start_date = values.dateRange[0].format('YYYY-MM-DD');
      params.end_date = values.dateRange[1].format('YYYY-MM-DD');
      delete params.dateRange;
    }
    setCurrentPage(1);
    fetchLogs(params);
  };

  const handleTableChange = (newPagination) => {
    const { current, pageSize: newPageSize } = newPagination;
    setCurrentPage(current);
    setPageSize(newPageSize);
    const values = form.getFieldsValue();
    fetchLogs({ ...values, page: current, pageSize: newPageSize });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '操作时间',
      dataIndex: 'created_at',
      width: 150,
      render: (text) => text ? text.slice(0, 16) : '-'
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      width: 120,
      render: (action) => <Tag color={actionColors[action] || 'default'}>{actionNames[action] || action}</Tag>
    },
    { title: '用户名', dataIndex: 'username', width: 100 },
    { title: '姓名', dataIndex: 'user_name', width: 100 },
    { title: '角色', dataIndex: 'role', width: 100 },
    { title: '对象类型', dataIndex: 'target_type', width: 100 },
    { title: '对象编号', dataIndex: 'target_no', width: 150 },
    {
      title: '设备类型',
      dataIndex: 'device_type',
      width: 150,
      render: (device_type) => (
        <Tooltip title={device_type}>
          {DEVICE_TYPE_MAP[device_type] || device_type || '-'}
        </Tooltip>
      )
    },
    {
      title: '详情',
      dataIndex: 'detail',
      width: 280,
      render: (detail) => {
        if (!detail) return '-';
        try {
          const obj = typeof detail === 'string' ? JSON.parse(detail) : detail;
          return (
            <div style={{ fontSize: 12, lineHeight: '1.6' }}>
              {Object.entries(obj).map(([key, value]) => {
                const label = detailKeyMap[key] || key;
                let displayValue = value;
                const mapper = detailValueMap[key];
                if (mapper !== undefined) {
                  if (typeof mapper === 'function') {
                    displayValue = mapper(value);
                  } else if (mapper[value] !== undefined) {
                    displayValue = mapper[value];
                  }
                }
                return (
                  <div key={key} style={{ marginBottom: 2 }}>
                    <span style={{ color: getTextColorSecondary() }}>{label}:</span>{' '}
                    <span style={{ color: getTextColor() }}>{displayValue !== null && displayValue !== undefined ? String(displayValue) : '-'}</span>
                  </div>
                );
              })}
            </div>
          );
        } catch (e) {
          return String(detail);
        }
      }
    },
    { title: 'IP地址', dataIndex: 'ip_address', width: 130 }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 24 }}>
        <h2 style={{ color: getTextColor() }}>系统操作日志</h2>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card style={{ 
              background: getContentBg(),
              border: `1px solid ${getBorderColor()}`
            }}>
              <Statistic title={<span style={{ color: getTextColorSecondary() }}>今日操作数</span>} value={stats.todayCount} valueStyle={{ color: getTextColor() }} />
            </Card>
          </Col>
          <Col span={18}>
            <Card 
              title={<span style={{ color: getTextColor() }}>操作类型分布</span>}
              style={{ 
                background: getContentBg(),
                border: `1px solid ${getBorderColor()}`
              }}
              headStyle={{ color: getTextColor(), borderBottom: `1px solid ${getBorderColor()}` }}
            >
              <Row gutter={16}>
                {stats.actionStats.map(s => (
                  <Col key={s.action} span={4}>
                    <div style={{ textAlign: 'center' }}>
                      <Tag color={actionColors[s.action] || 'default'}>{actionNames[s.action] || s.action}</Tag>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: getTextColor() }}>{s.count}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        <Form form={form} layout="inline" onFinish={handleSearch} style={{ 
          marginBottom: 24, 
          background: isDarkMode ? '#1f1f1f' : '#f5f5f5', 
          padding: 20, 
          borderRadius: 8,
          border: `1px solid ${getBorderColor()}`
        }}>
          <Form.Item name="username" label={<span style={{ color: getTextColor() }}>用户名</span>}>
            <Input placeholder="输入用户名" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="action" label={<span style={{ color: getTextColor() }}>操作类型</span>}>
            <Select placeholder="请选择" allowClear style={{ width: 150 }}>
              {actions.map(a => <Select.Option key={a} value={a}>{actionNames[a] || a}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="target_no" label={<span style={{ color: getTextColor() }}>对象编号</span>}>
            <Input placeholder="图纸编号等" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="dateRange" label={<span style={{ color: getTextColor() }}>时间范围</span>}>
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
            <Button style={{ marginLeft: 8 }} icon={<ReloadOutlined />} onClick={() => { form.resetFields(); fetchLogs(); }}>重置</Button>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          bordered
          scroll={{ x: 1500 }}
        />
      </Content>
    </Layout>
  );
};

export default SystemLogPage;